//@ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Empty, Input, Select, Spin, Tag, Tooltip, message } from "antd";
import {
  ReloadOutlined,
  DeleteOutlined,
  SearchOutlined,
  DownOutlined,
  RightOutlined,
  CopyOutlined,
  FileTextOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getAppLogs, clearAppLogs, type AppLogEntry } from "../../utils/API";

// Hidden debug page — deliberately NOT linked from the navbar or anywhere in the
// UI. Reached only by typing /logs in the address bar. It renders the backend's
// JSON Lines application log (logs/app.jsonl) with filtering, so a failed vendor
// form submission can be diagnosed without shell access to the server.

const BRAND = "#004AAD";

// One colour per level, used for the row accent, the level badge and the filter
// chips so a scan of the list immediately shows where the failures are.
const LEVEL_STYLE: Record<string, { color: string; bg: string; border: string }> = {
  ERROR: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
  WARN: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  INFO: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  DEBUG: { color: "#4b5563", bg: "#f9fafb", border: "#e5e7eb" },
  RAW: { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
};
const levelStyle = (lvl: string) => LEVEL_STYLE[lvl] || LEVEL_STYLE.DEBUG;

const LEVEL_OPTIONS = ["ERROR", "WARN", "INFO", "DEBUG", "RAW"];

const STAGE_OPTIONS = [
  { label: "All stages", value: "" },
  { label: "Vendor form submit", value: "vendor-submit" },
  { label: "Vendor token auth", value: "vendor-token-auth" },
  { label: "Octanist apply", value: "octanist-apply" },
  { label: "Octanist cron", value: "octanist-cron" },
  { label: "Octanist run URL", value: "octanist-run-url" },
  { label: "Octanist lead check", value: "octanist-lead-check" },
];

const SINCE_OPTIONS = [
  { label: "All time", value: "" },
  { label: "Last 15 minutes", value: "15m" },
  { label: "Last hour", value: "1h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
];

const sinceToIso = (v: string): string | undefined => {
  if (!v) return undefined;
  const mult: Record<string, number> = { m: 60e3, h: 3600e3, d: 86400e3 };
  const n = parseInt(v, 10);
  const unit = v.slice(-1);
  if (!Number.isFinite(n) || !mult[unit]) return undefined;
  return new Date(Date.now() - n * mult[unit]).toISOString();
};

const fmtTime = (ts: string | null) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const fmtBytes = (n: number) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

// Pull the handful of context fields that identify a log line at a glance, so
// the collapsed row is useful without expanding it.
const SUMMARY_KEYS = [
  "reason",
  "companyName",
  "email",
  "wtbId",
  "row",
  "itemCount",
  "createdCount",
  "qid",
  "to",
  "totalMs",
  "dbMs",
];

const LogRow: React.FC<{ entry: AppLogEntry; index: number }> = ({ entry, index }) => {
  const [open, setOpen] = useState(false);
  const st = levelStyle(entry.level);
  const ctx = entry.context || {};
  const hasDetail = Boolean(entry.error || Object.keys(ctx).length);

  // The single most useful line when something failed: the DB/validation reason
  // rather than the generic error message.
  const failureReason =
    entry.error?.dbDetail ||
    entry.error?.dbMessage ||
    entry.error?.validation?.map((v) => `${v.field}: ${v.message}`).join("; ") ||
    entry.error?.message ||
    null;

  const summary = SUMMARY_KEYS.filter(
    (k) => ctx[k] !== undefined && ctx[k] !== null && ctx[k] !== "",
  ).map((k) => ({ k, v: ctx[k] }));

  const copyEntry = () => {
    navigator.clipboard
      ?.writeText(JSON.stringify(entry, null, 2))
      .then(() => message.success("Log entry copied"))
      .catch(() => message.error("Could not copy"));
  };

  return (
    <div
      style={{
        borderBottom: "1px solid #f1f5f9",
        background: index % 2 === 0 ? "#ffffff" : "#fcfdff",
        borderLeft: `3px solid ${st.color}`,
      }}
    >
      <div
        onClick={() => hasDetail && setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "10px 14px",
          cursor: hasDetail ? "pointer" : "default",
        }}
      >
        <span style={{ width: 14, paddingTop: 3, color: "#94a3b8", flexShrink: 0 }}>
          {hasDetail ? (open ? <DownOutlined /> : <RightOutlined />) : null}
        </span>

        <span
          style={{
            flexShrink: 0,
            width: 62,
            textAlign: "center",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.04em",
            padding: "2px 0",
            borderRadius: 5,
            color: st.color,
            background: st.bg,
            border: `1px solid ${st.border}`,
          }}
        >
          {entry.level}
        </span>

        <span
          style={{
            flexShrink: 0,
            width: 155,
            fontSize: 12,
            color: "#64748b",
            fontFamily: "ui-monospace, Menlo, Consolas, monospace",
          }}
        >
          {fmtTime(entry.timestamp)}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}>
            {entry.message}
          </div>

          {failureReason && (
            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                color: "#b91c1c",
                fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                wordBreak: "break-word",
              }}
            >
              {failureReason}
            </div>
          )}

          {summary.length > 0 && (
            <div style={{ marginTop: 5, display: "flex", flexWrap: "wrap", gap: 5 }}>
              {summary.map(({ k, v }) => (
                <span
                  key={k}
                  style={{
                    fontSize: 11,
                    color: "#475569",
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: 5,
                    padding: "1px 6px",
                    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                  }}
                >
                  <span style={{ color: "#94a3b8" }}>{k}=</span>
                  {typeof v === "object" ? JSON.stringify(v) : String(v)}
                </span>
              ))}
            </div>
          )}
        </div>

        {ctx.requestId && (
          <Tooltip title="Correlation id — filter by this to see the whole submission">
            <span
              style={{
                flexShrink: 0,
                fontSize: 11,
                color: BRAND,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 5,
                padding: "1px 6px",
                fontFamily: "ui-monospace, Menlo, Consolas, monospace",
              }}
            >
              {ctx.requestId}
            </span>
          </Tooltip>
        )}
      </div>

      {open && (
        <div style={{ padding: "0 14px 14px 100px" }}>
          {entry.error && (
            <div style={{ marginBottom: 10 }}>
              <div style={labelCls}>Error</div>
              <pre style={{ ...preCls, background: "#fef2f2", borderColor: "#fecaca" }}>
                {JSON.stringify(entry.error, null, 2)}
              </pre>
            </div>
          )}
          {Object.keys(ctx).length > 0 && (
            <div>
              <div style={labelCls}>Context</div>
              <pre style={preCls}>{JSON.stringify(ctx, null, 2)}</pre>
            </div>
          )}
          <Button size="small" icon={<CopyOutlined />} onClick={copyEntry} style={{ marginTop: 8 }}>
            Copy JSON
          </Button>
        </div>
      )}
    </div>
  );
};

const labelCls: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#94a3b8",
  marginBottom: 4,
};

const preCls: React.CSSProperties = {
  margin: 0,
  padding: "10px 12px",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  fontSize: 12,
  lineHeight: 1.55,
  color: "#334155",
  overflowX: "auto",
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const LogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [entries, setEntries] = useState<AppLogEntry[]>([]);
  const [meta, setMeta] = useState<{ total: number; counts: Record<string, number>; file: any }>({
    total: 0,
    counts: {},
    file: {},
  });
  const [error, setError] = useState<string | null>(null);

  // Filters. Levels default to everything except DEBUG — DEBUG is verbose
  // step tracing that would otherwise bury the errors.
  const [levels, setLevels] = useState<string[]>(["ERROR", "WARN", "INFO"]);
  const [stage, setStage] = useState("vendor-submit");
  const [since, setSince] = useState("24h");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Committed search term — the request only fires on Enter / button click, so
  // typing does not spam the backend.
  const [appliedSearch, setAppliedSearch] = useState("");

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (opts.silent) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await getAppLogs({
          level: levels.length ? levels.join(",") : undefined,
          stage: stage || undefined,
          q: appliedSearch || undefined,
          since: sinceToIso(since),
          limit,
          includePrevious: true,
        });
        setEntries(Array.isArray(res.data) ? res.data : []);
        setMeta({ total: res.total || 0, counts: res.counts || {}, file: res.file || {} });
        setError(null);
      } catch (e: any) {
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load logs. Is the backend running?",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [levels, stage, appliedSearch, since, limit],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Live tail. Kept off by default so the page does not poll unless asked.
  const timerRef = useRef<any>(null);
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh) {
      timerRef.current = setInterval(() => load({ silent: true }), 5000);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoRefresh, load]);

  const applySearch = () => setAppliedSearch(search.trim());

  // This page renders its own minimal bar, so it carries its own logout
  // control rather than relying on a shared navigation component.
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/", { replace: true });
  };

  const handleClear = async () => {
    // Destructive and irreversible — the log file is truncated on the server.
    if (!window.confirm("Clear the entire backend log file? This cannot be undone.")) return;
    try {
      await clearAppLogs();
      message.success("Log file cleared");
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || "Failed to clear logs");
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(entries, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `app-logs-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "18px 20px 40px", background: "#f1f4f8", minHeight: "100vh" }}>
      {/* ---- Header ---- */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            display: "flex",
            height: 38,
            width: 38,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            background: BRAND,
            color: "#fff",
          }}
        >
          <FileTextOutlined style={{ fontSize: 17 }} />
        </span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}>
            Application Logs
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Backend log tail — vendor form submissions, token validation and email delivery.
            {meta.file?.path && (
              <>
                {" "}
                <span style={{ color: "#94a3b8" }}>
                  ({meta.file.path} · {fmtBytes(meta.file.size || 0)} · updated{" "}
                  {fmtTime(meta.file.lastModified)})
                </span>
              </>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            icon={autoRefresh ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => setAutoRefresh((v) => !v)}
            style={autoRefresh ? { borderColor: BRAND, color: BRAND } : undefined}
          >
            {autoRefresh ? "Live (5s)" : "Go live"}
          </Button>
          <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => load()}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={downloadJson} disabled={!entries.length}>
            Export
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={handleClear}>
            Clear
          </Button>

          {/* Divider — logout is a session action, not a log action. */}
          <span style={{ width: 1, background: "#e2e8f0", margin: "2px 4px" }} />

          <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </div>

      {/* ---- Filters ---- */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
        }}
      >
        {/* Level toggles. Only five values, and they are the filter used most
            often, so they are always-visible chips rather than a multi-select
            dropdown that collapses its tags to "+2 …" when space is tight. */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {LEVEL_OPTIONS.map((lvl) => {
            const on = levels.includes(lvl);
            const st = levelStyle(lvl);
            return (
              <button
                key={lvl}
                onClick={() =>
                  setLevels((prev) =>
                    prev.includes(lvl) ? prev.filter((l) => l !== lvl) : [...prev, lvl],
                  )
                }
                title={`${on ? "Hide" : "Show"} ${lvl} entries`}
                style={{
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                  padding: "5px 11px",
                  borderRadius: 7,
                  transition: "all .12s",
                  color: on ? st.color : "#94a3b8",
                  background: on ? st.bg : "#fff",
                  border: `1px solid ${on ? st.color : "#e2e8f0"}`,
                  opacity: on ? 1 : 0.65,
                }}
              >
                {lvl}
                {meta.counts[lvl] ? (
                  <span style={{ marginLeft: 6, fontWeight: 800 }}>{meta.counts[lvl]}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <span style={{ width: 1, alignSelf: "stretch", background: "#e2e8f0" }} />

        <Select
          value={stage}
          onChange={setStage}
          style={{ width: 190 }}
          options={STAGE_OPTIONS}
        />
        <Select value={since} onChange={setSince} style={{ width: 155 }} options={SINCE_OPTIONS} />
        <Select
          value={limit}
          onChange={setLimit}
          style={{ width: 120 }}
          options={[100, 200, 500, 1000].map((n) => ({ label: `${n} rows`, value: n }))}
        />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={applySearch}
          allowClear
          onClear={() => {
            setSearch("");
            setAppliedSearch("");
          }}
          placeholder="Search message, company, email, model, requestId…"
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          style={{ flex: 1, minWidth: 240 }}
        />
        <Button type="primary" onClick={applySearch} style={{ background: BRAND }}>
          Search
        </Button>
      </div>

      {/* ---- Result count. Per-level counts live on the toggle chips above. ---- */}
      {!loading && !error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            fontSize: 12,
            color: "#64748b",
          }}
        >
          <span>
            Showing <b style={{ color: "#334155" }}>{entries.length}</b> of{" "}
            <b style={{ color: "#334155" }}>{meta.total}</b> matching{" "}
            {meta.total === 1 ? "entry" : "entries"}
          </span>
          {meta.total > entries.length && (
            <span style={{ color: "#94a3b8" }}>
              — raise the row limit to see older ones
            </span>
          )}
          {appliedSearch && (
            <Tag
              closable
              onClose={() => {
                setSearch("");
                setAppliedSearch("");
              }}
              style={{ margin: 0 }}
            >
              search: {appliedSearch}
            </Tag>
          )}
        </div>
      )}

      {/* ---- Log list ---- */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ padding: 60, textAlign: "center" }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{ color: "#b91c1c", fontWeight: 600, marginBottom: 6 }}>{error}</div>
            <Button onClick={() => load()} icon={<ReloadOutlined />}>
              Retry
            </Button>
          </div>
        ) : !entries.length ? (
          <div style={{ padding: 50 }}>
            <Empty
              description={
                <span style={{ color: "#64748b", fontSize: 13 }}>
                  No log entries match these filters. Try widening the time range, clearing the
                  stage filter, or enabling the DEBUG level.
                </span>
              }
            />
          </div>
        ) : (
          entries.map((entry, i) => (
            <LogRow key={`${entry.timestamp}-${i}`} entry={entry} index={i} />
          ))
        )}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
        Internal debug page · not linked in navigation · tokens and passwords are redacted before
        they are written to the log.
      </div>
    </div>
  );
};

export default LogsPage;
